import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Image,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import {
  IconCalendarEvent,
  IconCheck,
  IconCopy,
  IconMapPin,
  IconShare2,
  IconUser,
  IconVideo,
} from "@tabler/icons-react";
import { ReactNode, useState } from "react";
import { IWebinar } from "../../../../models/interfaces/IWebinar.interface";
import {
  formatEventDate,
  formatEventTime,
  getEventTiming,
  isJoinOpen,
} from "./webinar.utils";

type WebinarCardProps = {
  event: IWebinar;
  now: number;
  onShare: (event: IWebinar) => void;
  onJoin: (event: IWebinar) => void;
};

function DetailRow({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <Flex gap="sm" align="flex-start">
      <Flex c="primary.6" pt={2} style={{ flexShrink: 0 }}>
        {icon}
      </Flex>
      <Box miw={0}>
        <Text fz="sm" fw={600}>
          {label}
        </Text>
        {children}
      </Box>
    </Flex>
  );
}

export function WebinarCard({ event, now, onShare, onJoin }: WebinarCardProps) {
  const clipboard = useClipboard({ timeout: 1500 });
  const [hasPosterError, setHasPosterError] = useState(false);

  const title = event.event_title?.trim();
  const poster = event.fileInfo?.view_page;
  const isOnline = event.event_type === "online";
  const isPublished = event.status === "published";
  const timing = getEventTiming(event.start_date, event.end_date, now);
  const canJoin = isJoinOpen(event.start_date, event.end_date, now);
  const showJoin = isOnline && !timing.hasEnded;

  return (
    <Card withBorder radius="lg" shadow="xs" h="100%">
      <Flex gap="md" align="flex-start">
        <Stack gap="xs" align="flex-start" miw={0} style={{ flex: 1 }}>
          <Badge
            variant={timing.isLive ? "dot" : "light"}
            color={timing.color}
            radius="sm"
          >
            {timing.label}
          </Badge>
          <Text
            component="h3"
            m={0}
            fw={700}
            fz={{ base: "md", sm: "lg" }}
            lh={1.3}
            lineClamp={3}
            title={title}
          >
            {title}
          </Text>
        </Stack>
        {poster && !hasPosterError && (
          <Anchor
            href={poster}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open poster for ${title}`}
            style={{ flexShrink: 0 }}
          >
            <Image
              src={poster}
              alt=""
              w={{ base: 64, sm: 80 }}
              h={{ base: 80, sm: 100 }}
              radius="md"
              fit="cover"
              onError={() => setHasPosterError(true)}
            />
          </Anchor>
        )}
      </Flex>

      <Stack gap="sm" my="md">
        <DetailRow
          icon={<IconCalendarEvent size={18} />}
          label={formatEventDate(event.start_date, event.end_date)}
        >
          <Text fz="sm" c="dimmed">
            {formatEventTime(event.start_date, event.end_date)}
          </Text>
        </DetailRow>

        <DetailRow
          icon={isOnline ? <IconVideo size={18} /> : <IconMapPin size={18} />}
          label={isOnline ? "Online" : "In person"}
        >
          {isOnline ? (
            <Group gap={4} wrap="nowrap">
              <Anchor
                href={event.event_detail}
                target="_blank"
                rel="noopener noreferrer"
                fz="sm"
              >
                Webinar link
              </Anchor>
              {isPublished && (
                <Tooltip
                  label={clipboard.copied ? "Copied" : "Copy link"}
                  withArrow
                >
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label="Copy webinar link"
                    onClick={() => clipboard.copy(event.event_detail)}
                  >
                    {clipboard.copied ? (
                      <IconCheck size={16} />
                    ) : (
                      <IconCopy size={16} />
                    )}
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          ) : (
            <Text fz="sm" c="dimmed" lineClamp={2}>
              {event.event_detail}
            </Text>
          )}
        </DetailRow>

        {event.host && (
          <DetailRow icon={<IconUser size={18} />} label="Host">
            <Text fz="sm" c="dimmed" lineClamp={2}>
              {event.host}
            </Text>
          </DetailRow>
        )}
      </Stack>

      {isPublished && (
        <Card.Section withBorder inheritPadding py="sm" mt="auto">
          <Flex align="center" justify="flex-end" wrap="wrap" gap="xs">
            {showJoin && !canJoin && (
              <Text fz="xs" c="dimmed" style={{ flex: "1 1 160px" }}>
                Join opens 10 min before start
              </Text>
            )}
            <Group gap="xs" wrap="nowrap">
              <Button
                variant="default"
                size="sm"
                leftSection={<IconShare2 size={16} />}
                onClick={() => onShare(event)}
              >
                Share
              </Button>
              {showJoin && (
                <Button
                  size="sm"
                  disabled={!canJoin}
                  onClick={() => onJoin(event)}
                >
                  Join
                </Button>
              )}
            </Group>
          </Flex>
        </Card.Section>
      )}
    </Card>
  );
}
